import { Controller } from "@hotwired/stimulus";

/**
 * PWA Install Prompt Controller
 *
 * Manages the PWA installation prompt banner, handling browser install events
 * and user dismissal preferences. Supports both temporary (7-day) and permanent
 * dismissal options.
 *
 * @extends Controller
 */
export default class extends Controller {
  static targets = ["banner", "dontAskCheckbox"];

  /**
   * Initialize the controller and set up event listeners
   */
  connect() {
    // Allow manual triggering with URL parameter
    if (window.location.search.includes("show-install")) {
      this.showBanner();
      return;
    }

    if (this.isInstalled() || this.isDismissed()) {
      this.hideBanner();
      return;
    }

    this.deferredPrompt = null;
    this.setupEventListeners();
    this.checkInstallationStatus();
  }

  /**
   * Set up browser event listeners for PWA installation
   * @private
   */
  setupEventListeners() {
    // Capture the browser's install prompt event
    window.addEventListener("beforeinstallprompt", (e) => {
      e.preventDefault();
      this.deferredPrompt = e;
      this.showBanner();
    });

    // Handle successful app installation
    window.addEventListener("appinstalled", () => {
      this.hideBanner();
      localStorage.setItem("pwa-installed", "true");
    });
  }

  /**
   * Check if app is already installed and update status
   * @private
   */
  checkInstallationStatus() {
    // Check if running in standalone mode (already installed)
    if (window.matchMedia("(display-mode: standalone)").matches) {
      this.hideBanner();
      localStorage.setItem("pwa-installed", "true");
    }
  }

  /**
   * Trigger the browser's PWA installation prompt
   * @async
   */
  async install() {
    if (!this.deferredPrompt) {
      return;
    }

    const result = await this.deferredPrompt.prompt();
    this.deferredPrompt = null;
    this.hideBanner();

    if (result.outcome === "accepted") {
      localStorage.setItem("pwa-installed", "true");
    }
  }

  /**
   * Handle temporary dismissal (7 days) or permanent based on checkbox
   */
  dismissTemporary() {
    if (this.hasDontAskCheckboxTarget && this.dontAskCheckboxTarget.checked) {
      this.dismissPermanently();
    } else {
      const dismissedUntil = new Date();
      dismissedUntil.setDate(dismissedUntil.getDate() + 7);
      localStorage.setItem(
        "pwa-install-dismissed-until",
        dismissedUntil.toISOString(),
      );
    }

    this.hideBanner();
  }

  /**
   * Permanently dismiss the install prompt
   */
  dismissPermanently() {
    localStorage.setItem("pwa-install-dismissed-permanently", "true");
    this.hideBanner();
  }

  /**
   * Show the install prompt banner
   * @private
   */
  showBanner() {
    if (this.hasBannerTarget) {
      this.bannerTarget.classList.remove("hidden");
    }
  }

  /**
   * Hide the install prompt banner
   * @private
   */
  hideBanner() {
    if (this.hasBannerTarget) {
      this.bannerTarget.classList.add("hidden");
    }
  }

  /**
   * Check if the PWA is already installed
   * @returns {boolean} True if app is installed
   * @private
   */
  isInstalled() {
    return (
      localStorage.getItem("pwa-installed") === "true" ||
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true
    );
  }

  /**
   * Check if the prompt has been dismissed (temporarily or permanently)
   * @returns {boolean} True if prompt should not be shown
   * @private
   */
  isDismissed() {
    // Check permanent dismissal
    if (localStorage.getItem("pwa-install-dismissed-permanently") === "true") {
      return true;
    }

    // Check temporary dismissal
    const dismissedUntil = localStorage.getItem("pwa-install-dismissed-until");
    if (!dismissedUntil) return false;

    const dismissedDate = new Date(dismissedUntil);
    const now = new Date();

    // Clean up expired dismissal
    if (dismissedDate <= now) {
      localStorage.removeItem("pwa-install-dismissed-until");
      return false;
    }

    return true;
  }
}
